import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr } from "../api";
import { useAuth } from "../AuthContext";
import { Btn, ConfirmDialog, Modal, Field, inputCls } from "../components";

const blank = { title: "", issuing_authority: "", cert_number: "", issue_date: "", expiry_date: "", description: "", doc_url: "", active: true, order: 0 };

export const uploadDoc = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await adminApi.post("/files", fd);
  return r.data;
};

const CertsAdmin = () => {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => adminApi.get("/certifications").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...edit, order: Number(edit.order) || 0 };
      if (edit.id) await adminApi.put(`/certifications/${edit.id}`, payload);
      else await adminApi.post("/certifications", payload);
      toast.success(edit.id ? "Certification updated" : "Certification added");
      setEdit(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  const pickDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await uploadDoc(f);
      setEdit({ ...edit, doc_url: r.url });
      toast.success("Document uploaded");
    } catch (err) { toast.error(fmtErr(err)); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggle = async (c) => {
    try {
      await adminApi.put(`/certifications/${c.id}`, { title: c.title, issuing_authority: c.issuing_authority, cert_number: c.cert_number, issue_date: c.issue_date, expiry_date: c.expiry_date, description: c.description, doc_url: c.doc_url, order: c.order, active: !c.active });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await adminApi.delete(`/certifications/${del.id}`);
      toast.success("Certification deleted");
      setDel(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-certifications-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Certifications & Approvals</h1>
          <p className="mt-1 text-sm text-slate-500">Only active certifications are shown publicly. Upload genuine certificate PDFs or images.</p>
        </div>
        <Btn onClick={() => setEdit({ ...blank })} data-testid="add-cert-btn"><Plus size={14} /> Add Certification</Btn>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {["Title", "Issuing Authority", "Document", "Order", "Status", "Actions"].map((h) => (
                <th key={h} className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No certifications added yet.</td></tr>
            ) : (
              items.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cert-row-${c.id}`}>
                  <td className="px-5 py-3 font-semibold text-slate-900">{c.title}</td>
                  <td className="px-5 py-3 text-slate-600">{c.issuing_authority || "—"}</td>
                  <td className="px-5 py-3">
                    {c.doc_url ? <a href={c.doc_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#C5221F] hover:underline"><FileText size={13} /> View</a> : <span className="text-slate-300 text-xs">None</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{c.order}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggle(c)} className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 border rounded-sm ${c.active ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-200 text-slate-500 border-slate-300"}`} data-testid={`cert-active-${c.id}`}>
                      {c.active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setEdit({ ...c })} className="p-2 text-slate-500 hover:text-[#C5221F]" data-testid={`cert-edit-${c.id}`}><Pencil size={15} /></button>
                      {user.role === "super_admin" && (
                        <button onClick={() => setDel(c)} className="p-2 text-slate-400 hover:text-red-600" data-testid={`cert-delete-${c.id}`}><Trash2 size={15} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Edit Certification" : "Add Certification"} wide>
        {edit && (
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
            <Field label="Certification Title *"><input className={inputCls} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} required data-testid="cert-title-input" /></Field>
            <Field label="Issuing Authority"><input className={inputCls} value={edit.issuing_authority} onChange={(e) => setEdit({ ...edit, issuing_authority: e.target.value })} data-testid="cert-authority-input" /></Field>
            <Field label="Certificate Number"><input className={inputCls} value={edit.cert_number} onChange={(e) => setEdit({ ...edit, cert_number: e.target.value })} data-testid="cert-number-input" /></Field>
            <Field label="Display Order"><input type="number" className={inputCls} value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} data-testid="cert-order-input" /></Field>
            <Field label="Issue Date"><input className={inputCls} value={edit.issue_date} onChange={(e) => setEdit({ ...edit, issue_date: e.target.value })} placeholder="e.g. 08 Dec 2023" data-testid="cert-issue-input" /></Field>
            <Field label="Expiry Date"><input className={inputCls} value={edit.expiry_date} onChange={(e) => setEdit({ ...edit, expiry_date: e.target.value })} placeholder="e.g. 07 Dec 2026" data-testid="cert-expiry-input" /></Field>
            <div className="sm:col-span-2"><Field label="Short Description"><textarea rows={2} className={inputCls} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="cert-desc-input" /></Field></div>
            <div className="sm:col-span-2">
              <Field label="Certificate Document (PDF or image)">
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={pickDoc} data-testid="cert-file-input" />
                <div className="flex items-center gap-3">
                  <Btn variant="ghost" type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="cert-upload-btn">
                    {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {edit.doc_url ? "Replace Document" : "Upload Document"}
                  </Btn>
                  {edit.doc_url && <a href={edit.doc_url} target="_blank" rel="noreferrer" className="text-xs text-[#C5221F] hover:underline">Current document</a>}
                </div>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer sm:col-span-2">
              <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} className="w-4 h-4 accent-[#C5221F]" data-testid="cert-active-check" /> Active (visible publicly)
            </label>
            <div className="sm:col-span-2 flex justify-end gap-3 border-t border-slate-100 pt-4">
              <Btn variant="ghost" type="button" onClick={() => setEdit(null)}>Cancel</Btn>
              <Btn type="submit" disabled={busy} data-testid="cert-save-btn">{busy && <Loader2 size={13} className="animate-spin" />} Save</Btn>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog open={!!del} title="Delete this certification?" message={`"${del?.title}" will be permanently removed.`} onConfirm={doDelete} onClose={() => setDel(null)} busy={busy} />
    </div>
  );
};

export default CertsAdmin;
